import { Table } from "components/table";
import Pagination from "components/table/pagination";
import Button from "components/button";
import Icon from "components/icon";
import Select from "components/select";
import Typography from "components/typography";
import ConfirmationModal from "components/shared/confirmation-modal";
import HistoryPanel from "components/shared/history-panel";
import VehicleModelImportModal from "./VehicleModelImportModal";
import { useAppTranslation } from "locale/useAppTranslation";
import useVehicleModelList, {
  VehicleModelRow,
} from "./useVehicleModelList";
import { addVehicleModelPageRouteName } from "../add/add.page";

export const vehicleModelPageRouteName = "/master-data/vehicle-model";

const VehicleModelPage = () => {
  const { t } = useAppTranslation("vehicleModel");
  const {
    data,
    columns,
    sortState,
    setSortState,
    filterValues,
    setFilterValues,
    handleFilterChange,
    selectedKeys,
    setSelectedKeys,
    currentPage,
    setCurrentPage,
    rowsPerPage,
    setRowsPerPage,
    totalPages,
    navigate,
    bulkAction,
    setBulkAction,
    isBulkActionDisabled,
    showBulkConfirmModal,
    setShowBulkConfirmModal,
    handleApplyBulkAction,
    handleConfirmBulkAction,
    showImportModal,
    setShowImportModal,
    importFile,
    setImportFile,
    importErrors,
    isDownloadingTemplate,
    isImporting,
    handleDownloadTemplate,
    handleImport,
    handleCloseImportModal,
    showToggleConfirmModal,
    setShowToggleConfirmModal,
    toggleRow,
    handleConfirmToggleStatus,
    historyPanel,
    isLoading,
  } = useVehicleModelList();

  return (
    <div className="flex flex-col gap-6">
      {/* Top Action Bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Select
            options={[
              { label: t("btn-bulk-actions"), value: "" },
              { label: t("btn-activate"), value: "activate" },
              { label: t("btn-deactivate"), value: "deactivate" },
            ]}
            value={bulkAction}
            placeholder={t("btn-bulk-actions")}
            onValueChange={(val) => setBulkAction(val as string)}
            disabled={isBulkActionDisabled}
            data-testid="bulk-action-dropdown-model"
          />
          <Button
            type="button"
            variant="outline"
            color="tertiary"
            size="medium"
            onClick={handleApplyBulkAction}
            disabled={!bulkAction || isBulkActionDisabled}
            data-testid="btn-apply-data-model"
          >
            {t("btn-apply-data")}
          </Button>
        </div>
        <div className="flex items-center gap-3">
          <Button
            type="button"
            variant="outline"
            color="tertiary"
            size="medium"
            iconLeft={<Icon name="import" size={15} />}
            onClick={() => setShowImportModal(true)}
            data-testid="btn-import-vehicle-model"
          >
            {t("btn-import")}
          </Button>
          <Button
            type="button"
            variant="fill"
            color="primary"
            size="medium"
            iconLeft={<Icon name="add" size={16} />}
            onClick={() => navigate(addVehicleModelPageRouteName)}
            data-testid="btn-add-vehicle-model"
          >
            {t("btn-add-vehicle-model")}
          </Button>
        </div>
      </div>

      {/* Table */}
      <section className="rounded-2xl border border-border bg-white shadow-sm">
        <Table<VehicleModelRow>
          data={data}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          currentPage={currentPage}
          limit={rowsPerPage}
          selectable
          selectedRowKeys={selectedKeys}
          onSelectedRowKeysChange={(keys) => setSelectedKeys(keys)}
          sortState={sortState}
          onSortStateChange={setSortState}
          filterValues={filterValues}
          onFilterValuesChange={handleFilterChange}
          selectableLabel="All"
          data-testid="table-vehicle-model"
        />

        {/* Pagination Bar */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-border">
          <div className="flex items-center gap-2">
            <Typography variant="body" tone="muted" as="span">{t("rows-per-page")}</Typography>
            <select
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="border border-border rounded-lg px-2 py-1 text-sm bg-white cursor-pointer"
              data-testid="dropdown-rows-per-page"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>
          <div data-testid="pagination-vehicle-model">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        </div>
      </section>

      {/* Bulk Action Confirmation Modal */}
      <ConfirmationModal
        open={showBulkConfirmModal}
        title={t("modal-bulk-action-title")}
        message={t("modal-bulk-action-message")}
        confirmLabel={t("modal-ok")}
        cancelLabel={t("btn-cancel")}
        onConfirm={handleConfirmBulkAction}
        onCancel={() => setShowBulkConfirmModal(false)}
        testId="bulk-action-confirm-modal"
      />

      {/* Toggle Status Confirmation Modal */}
      <ConfirmationModal
        open={showToggleConfirmModal}
        title={toggleRow?.status === "active" ? t("modal-deactivate-title") : t("modal-activate-title")}
        message={toggleRow?.status === "active" ? t("modal-deactivate-message") : t("modal-activate-message")}
        confirmLabel={t("modal-ok")}
        cancelLabel={t("btn-cancel")}
        onConfirm={handleConfirmToggleStatus}
        onCancel={() => setShowToggleConfirmModal(false)}
        testId="toggle-status-confirm-modal"
      />

      {/* History Panel */}
      <HistoryPanel
        open={historyPanel.isOpen}
        onClose={historyPanel.closePanel}
        historyData={historyPanel.historyData}
        groupedEntries={historyPanel.groupedEntries}
        filterAction={historyPanel.filterAction}
        onFilterChange={historyPanel.setFilterAction}
        currentPage={historyPanel.currentPage}
        totalPages={historyPanel.totalPages}
        onPageChange={historyPanel.setCurrentPage}
        testIdPrefix="history-panel-model"
      />

      {/* Import Modal */}
      <VehicleModelImportModal
        open={showImportModal}
        onClose={handleCloseImportModal}
        onDownloadTemplate={handleDownloadTemplate}
        isDownloading={isDownloadingTemplate}
        selectedFile={importFile}
        onFileSelect={setImportFile}
        onUpload={handleImport}
        isUploading={isImporting}
        importErrors={importErrors}
      />
    </div>
  );
};

export default VehicleModelPage;
